import { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "@/app/hooks";
import {
  shellApi,
  useLazyGetNotificationsQuery,
  useReadAllNotificationsMutation,
  useReadNotificationMutation,
} from "@/features/shell/api";
import type { Notification } from "@/shared/types/notification";
import { NOTIFICATION_PAGE_SIZE } from "./codes";

/** Lista paginata della modale notifiche (NotificationsModal.jsx: page, onReachEnd, refreshNotifications, readNotification, readAll). */
export function useNotificationList(active: boolean) {
  const dispatch = useAppDispatch();
  const [fetchPage] = useLazyGetNotificationsQuery();
  const [readOne] = useReadNotificationMutation();
  const [readAll] = useReadAllNotificationsMutation();
  const [items, setItems] = useState<Notification[]>([]);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // `endNotification` del legacy: la pagina e' piena, potrebbe essercene un'altra.
  const pageFull = items.length === page * NOTIFICATION_PAGE_SIZE;

  // `subscribe: false`: una rilettura puntuale, senza lasciare una sottoscrizione per chiamata; il
  // fulfilled arriva comunque al ponte, che aggiorna il contatore legacy.
  const refreshCounter = useCallback(
    () =>
      dispatch(
        shellApi.endpoints.getNotificationsCount.initiate(undefined, {
          forceRefetch: true,
          subscribe: false,
        }),
      ),
    [dispatch],
  );

  const loadFirstPage = useCallback(async () => {
    const data = await fetchPage({ from: 0, size: NOTIFICATION_PAGE_SIZE }).unwrap();
    setItems(data);
    setPage(1);
  }, [fetchPage]);

  useEffect(() => {
    if (!active) return;
    // Sincronizza con il sistema esterno (fetch RTK Query): `loadFirstPage` aggiorna lo stato in un
    // `.then` dopo l'await, non in modo sincrono nel corpo dell'effetto; la regola non segue il confine
    // asincrono (come in RequireSession). Errore ingoiato: la lista resta quella precedente (il legacy
    // lasciava la rejection non gestita).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadFirstPage().catch(() => {});
  }, [active, loadFirstPage]);

  const loadMore = useCallback(() => {
    if (!pageFull || loadingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    setLoadingMore(true);
    void fetchPage({ from: (nextPage - 1) * NOTIFICATION_PAGE_SIZE + 1, size: NOTIFICATION_PAGE_SIZE })
      .unwrap()
      .then((data) => setItems((current) => [...current, ...data]))
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [fetchPage, loadingMore, page, pageFull]);

  const refresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([refreshCounter(), loadFirstPage()]);
    } catch {
      // il contatore resta quello precedente: nessun feedback bloccante (come il legacy)
    } finally {
      setRefreshing(false);
    }
  }, [loadFirstPage, refreshCounter, refreshing]);

  const markRead = useCallback(
    async (id: Notification["id"]) => {
      // unwrap: se la POST fallisce non si segna nulla e si rifiuta (il legacy si fermava sull'await axios.post).
      await readOne(id).unwrap();
      await refreshCounter();
      setItems((current) => current.map((item) => (item.id === id ? { ...item, readAt: Date.now() } : item)));
    },
    [readOne, refreshCounter],
  );

  const markAllRead = useCallback(async () => {
    await readAll().unwrap();
    await refreshCounter();
    setItems((current) =>
      current.map((item) => (item.readAt == null ? { ...item, readAt: Date.now() } : item)),
    );
  }, [readAll, refreshCounter]);

  return { items, loadingMore, refreshing, pageFull, loadMore, refresh, markRead, markAllRead };
}
